import { useEffect, useMemo, useState } from "react";
import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { z } from "zod";
import {
  BookOpen,
  Calendar,
  ChevronRight,
  Crown,
  Loader2,
  Medal,
  Plus,
  Send,
  Shield,
  Target,
  Trash2,
  Users,
  X,
} from "lucide-react";
import {
  useChangeMemberRole,
  useGroup,
  useGroupLeaderboard,
  useGroupMembers,
  useRemoveGroupMember,
  useSendGroupInvites,
} from "@/hooks/use-groups";
import { useAuth } from "@/hooks/use-auth";
import { useAllExams } from "@/hooks/use-exams";
import { cleanExamText } from "@/lib/exam-display";
import { GroupCapInquiryDialog } from "@/components/group-cap-inquiry-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiError } from "@/lib/api";
import { createErrorMessage } from "@/lib/error-message";
import { cn } from "@/lib/utils";
import type { Group, GroupRole, LeaderboardPeriod } from "@/lib/types";

const tabSchema = z.enum(["home", "manage"]).catch("home");

export const Route = createFileRoute("/_main/groups/$gid")({
  component: GroupDetailPage,
  validateSearch: z.object({ tab: tabSchema.optional() }),
});

function GroupDetailPage() {
  const { gid } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const pathname = useLocation({ select: (s) => s.pathname });
  const tab = search.tab ?? "home";

  // 이 라우트는 자식 라우트(/groups/$gid/exams/$slug/...)의 부모이기도
  // 하다. 정확히 /groups/$gid 일 때만 콘솔을 그리고, 그 외엔 자식
  // 라우트(시험 풀이 화면 등)를 Outlet으로 위임한다.
  const isExactConsole = pathname === `/groups/${gid}`;

  const { user } = useAuth();
  const { data: group, isLoading: groupLoading } = useGroup(gid);
  const { data: members = [] } = useGroupMembers(gid);

  const myRole: GroupRole | undefined = useMemo(() => {
    if (!user) return undefined;
    return members.find((m) => m.user_id === user.id)?.role;
  }, [members, user]);
  const isSiteAdmin = user?.role === "admin";
  const canManage = isSiteAdmin || myRole === "owner" || myRole === "admin";
  const canChangeRoles = isSiteAdmin || myRole === "owner";

  // ?tab=manage로 직접 들어왔는데 권한이 없으면 home으로 reset.
  // (콘솔이 아닌 자식 라우트일 땐 굳이 검사할 필요 없음)
  useEffect(() => {
    if (!isExactConsole) return;
    if (tab === "manage" && !canManage && !groupLoading) {
      navigate({
        to: "/groups/$gid",
        params: { gid },
        search: { tab: "home" },
        replace: true,
      });
    }
  }, [isExactConsole, tab, canManage, groupLoading, gid, navigate]);

  if (!isExactConsole) {
    return <Outlet />;
  }

  if (groupLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!group) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          그룹을 찾을 수 없습니다
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold">{group.name}</h1>
        {group.description && (
          <p className="mt-1 text-sm text-muted-foreground">
            {group.description}
          </p>
        )}
      </div>

      {tab === "manage" && canManage ? (
        <ManageTab
          gid={group.id}
          groupName={group.name}
          memberCount={group.member_count}
          canChangeRoles={canChangeRoles}
        />
      ) : (
        <HomeTab gid={group.id} group={group} />
      )}
    </div>
  );
}

function HomeTab({ gid, group }: { gid: string; group: Group }) {
  const { user } = useAuth();
  const [period, setPeriod] = useState<LeaderboardPeriod>("week");
  const [examFilter, setExamFilter] = useState<string>("__all__");
  const { data: rows = [], isLoading: lbLoading } = useGroupLeaderboard(gid, {
    period,
    exam: examFilter === "__all__" ? undefined : examFilter,
  });
  const { data: allExams = [] } = useAllExams();

  const myEntry = rows.find((r) => r.user_id === user?.id);
  const myAttempts = myEntry?.attempts ?? 0;
  const myAccuracy =
    myEntry && myEntry.attempts > 0
      ? `${myEntry.accuracy_rate.toFixed(1)}%`
      : "-";
  const lastStudy = user?.last_study_date || "-";

  const examMeta = new Map(allExams.map((e) => [e.id, e] as const));
  const groupExams = group.exams.map((slug) => examMeta.get(slug));

  const top5 = rows.slice(0, 5);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <KpiCard
          icon={<Target className="size-5" />}
          label={period === "week" ? "이번 주 푼 문제" : "이번 달 푼 문제"}
          value={myAttempts}
          suffix="문제"
        />
        <KpiCard
          icon={<Crown className="size-5" />}
          label={period === "week" ? "이번 주 정답률" : "이번 달 정답률"}
          value={myAccuracy}
        />
        <KpiCard
          icon={<Calendar className="size-5" />}
          label="마지막 학습일"
          value={lastStudy}
        />
      </div>

      <section>
        <h2 className="mb-3 text-base font-semibold">이 그룹의 시험</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {groupExams.map((exam, i) => {
            const slug = group.exams[i];
            const name = exam?.name ?? slug;
            const desc = exam?.description;
            return (
              <Link
                key={slug}
                to="/groups/$gid/exams/$slug"
                params={{ gid, slug }}
                className="block"
              >
                <Card className="h-full cursor-pointer transition-all hover:border-blue-300 hover:shadow-lg dark:hover:border-blue-700">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="rounded-lg bg-blue-50 p-2.5 dark:bg-blue-950">
                        <BookOpen className="h-5 w-5 text-blue-500" />
                      </div>
                      <ChevronRight className="h-5 w-5 text-zinc-300" />
                    </div>
                    <CardTitle className="mt-3 text-lg">
                      {cleanExamText(name)}
                    </CardTitle>
                    {desc && (
                      <CardDescription>{cleanExamText(desc)}</CardDescription>
                    )}
                  </CardHeader>
                </Card>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-base font-semibold">
            푼 문제 수 랭킹{" "}
            <span className="text-xs font-normal text-muted-foreground">
              {period === "week" ? "최근 7일" : "최근 30일"}
            </span>
          </h2>
          <div className="flex flex-wrap gap-2">
            <Tabs
              value={period}
              onValueChange={(v) => setPeriod(v as LeaderboardPeriod)}
            >
              <TabsList>
                <TabsTrigger value="week">금주</TabsTrigger>
                <TabsTrigger value="month">한달</TabsTrigger>
              </TabsList>
            </Tabs>
            <Select
              value={examFilter}
              onValueChange={(v) => setExamFilter(v ?? "__all__")}
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">전체 시험</SelectItem>
                {group.exams.map((s) => {
                  const meta = examMeta.get(s);
                  return (
                    <SelectItem key={s} value={s}>
                      {meta ? cleanExamText(meta.name) : s}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>

        {lbLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : top5.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              기간 내 기록이 없습니다
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {top5.map((r, i) => (
              <RankCard
                key={r.user_id}
                rank={i + 1}
                email={r.email}
                attempts={r.attempts}
                accuracy={r.accuracy_rate}
                isMe={r.user_id === user?.id}
              />
            ))}
          </div>
        )}
      </section>

    </div>
  );
}

function RankCard({
  rank,
  email,
  attempts,
  accuracy,
  isMe,
}: {
  rank: number;
  email: string;
  attempts: number;
  accuracy: number;
  isMe: boolean;
}) {
  // 메달 색은 Tailwind safelist 보장을 위해 cn으로 분기.
  const medalClass =
    rank === 1
      ? "text-amber-500"
      : rank === 2
        ? "text-zinc-400"
        : rank === 3
          ? "text-orange-700"
          : "text-muted-foreground";
  return (
    <Card
      className={cn(
        "flex flex-col items-center text-center transition-colors",
        isMe && "border-primary bg-primary/5",
      )}
    >
      <CardContent className="flex flex-col items-center gap-2 py-4">
        <Medal className={cn("size-7", medalClass)} />
        <span className="text-2xl font-bold tabular-nums">{rank}</span>
        <span className="line-clamp-1 max-w-full text-xs text-muted-foreground">
          {email}
        </span>
        <div className="flex flex-col items-center gap-0.5">
          <span className="text-sm font-semibold tabular-nums">
            {attempts}문제
          </span>
          <span className="text-xs text-muted-foreground tabular-nums">
            {attempts > 0 ? `${accuracy.toFixed(1)}%` : "-"}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function RoleIcon({ role }: { role: GroupRole }) {
  if (role === "owner")
    return <Crown className="size-4 shrink-0 text-amber-500" />;
  if (role === "admin")
    return <Shield className="size-4 shrink-0 text-blue-500" />;
  return <Users className="size-4 shrink-0 text-muted-foreground" />;
}

function ManageTab({
  gid,
  groupName,
  memberCount,
  canChangeRoles,
}: {
  gid: string;
  groupName: string;
  memberCount: number;
  canChangeRoles: boolean;
}) {
  const sendInvites = useSendGroupInvites(gid);
  const removeMember = useRemoveGroupMember(gid);
  const changeRole = useChangeMemberRole(gid);
  const { data: members = [] } = useGroupMembers(gid);

  const [current, setCurrent] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [result, setResult] = useState<{ processed: number } | null>(null);
  const [error, setError] = useState("");
  const [memberError, setMemberError] = useState("");
  const [capDialog, setCapDialog] = useState(false);
  const atCap = memberCount >= 50;

  function addOne(raw: string): boolean {
    const v = raw.trim().toLowerCase();
    if (!v) return false;
    if (!v.includes("@")) {
      setError("올바른 이메일 형식이 아닙니다");
      return false;
    }
    setError("");
    setEmails((prev) => (prev.includes(v) ? prev : [...prev, v]));
    return true;
  }

  function commitCurrent() {
    if (addOne(current)) setCurrent("");
  }

  function removeAt(idx: number) {
    setEmails((prev) => prev.filter((_, i) => i !== idx));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      if (current.trim()) {
        e.preventDefault();
        commitCurrent();
      }
    } else if (e.key === "Backspace" && !current && emails.length > 0) {
      e.preventDefault();
      setEmails((prev) => prev.slice(0, -1));
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData("text");
    const parts = text
      .split(/[\s,;\n]+/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.includes("@"));
    if (parts.length <= 1) return;
    e.preventDefault();
    setEmails((prev) => {
      const next = [...prev];
      for (const p of parts) if (!next.includes(p)) next.push(p);
      return next;
    });
    setCurrent("");
    setError("");
  }

  async function handleSubmit() {
    setError("");
    setResult(null);
    const all = [...emails];
    const trim = current.trim().toLowerCase();
    if (trim && trim.includes("@") && !all.includes(trim)) all.push(trim);
    if (all.length === 0) {
      setError("이메일을 1개 이상 추가해주세요");
      return;
    }
    try {
      const res = await sendInvites.mutateAsync(all);
      setResult(res);
      setEmails([]);
      setCurrent("");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setCapDialog(true);
        return;
      }
      setError(createErrorMessage(err));
    }
  }

  async function handleRemoveMember(uid: string) {
    setMemberError("");
    if (!confirm("이 멤버를 그룹에서 제거할까요?")) return;
    try {
      await removeMember.mutateAsync(uid);
    } catch (err) {
      setMemberError(createErrorMessage(err));
    }
  }

  async function handleRoleToggle(uid: string, current: GroupRole) {
    setMemberError("");
    const next = current === "admin" ? "member" : "admin";
    try {
      await changeRole.mutateAsync({ uid, role: next });
    } catch (err) {
      setMemberError(createErrorMessage(err));
    }
  }

  const trimmedCurrent = current.trim();
  const totalToSend =
    emails.length + (trimmedCurrent.includes("@") ? 1 : 0);

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>이메일로 초대</CardTitle>
          <CardDescription>
            한 번에 한 명씩 추가하고, 모아서 일괄 발송합니다. 가입된 사용자에게만
            초대가 전달되며 가입 여부는 표시되지 않습니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Label htmlFor="invite-email">이메일 추가</Label>
          {emails.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {emails.map((email, i) => (
                <span
                  key={`${email}-${i}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-sm"
                >
                  {email}
                  <button
                    type="button"
                    onClick={() => removeAt(i)}
                    aria-label={`${email} 제거`}
                    className="rounded-full text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <X className="size-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input
              id="invite-email"
              type="email"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder="user@example.com"
            />
            <Button
              type="button"
              variant="outline"
              onClick={commitCurrent}
              disabled={!current.trim()}
              aria-label="이메일 추가"
            >
              <Plus className="size-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Enter / 쉼표 / 공백으로 추가, 빈 입력에서 백스페이스로 마지막 항목
            제거
          </p>
          {atCap && (
            <p className="rounded-lg bg-amber-100/60 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              그룹 인원이 가득 차 새 초대가 자동으로 무시됩니다.{" "}
              <button
                type="button"
                onClick={() => setCapDialog(true)}
                className="underline"
              >
                관리자에게 문의
              </button>
            </p>
          )}
          {result && (
            <p className="rounded-lg bg-emerald-100/50 px-3 py-2 text-sm text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
              {result.processed}건 처리되었습니다
            </p>
          )}
          {error && (
            <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {totalToSend > 0 ? `${totalToSend}건 발송 대기` : ""}
            </span>
            <Button
              onClick={handleSubmit}
              disabled={sendInvites.isPending || totalToSend === 0}
            >
              <Send className="size-4" />
              일괄 초대 보내기
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>멤버 관리</CardTitle>
          <CardDescription>
            역할 변경 또는 그룹에서 제거할 수 있습니다.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="flex flex-col">
            {members.map((m) => (
              <li
                key={m.user_id}
                className="flex items-center gap-3 border-b py-3 last:border-b-0"
              >
                <RoleIcon role={m.role} />
                <span className="flex-1 truncate text-sm">{m.email}</span>
                <Badge variant="outline" className="text-[11px]">
                  {m.role}
                </Badge>
                {canChangeRoles && m.role !== "owner" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRoleToggle(m.user_id, m.role)}
                    disabled={changeRole.isPending}
                  >
                    {m.role === "admin" ? "→ member" : "→ admin"}
                  </Button>
                )}
                {m.role !== "owner" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveMember(m.user_id)}
                    disabled={removeMember.isPending}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
          {memberError && (
            <p className="mt-3 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {memberError}
            </p>
          )}
        </CardContent>
      </Card>

      <GroupCapInquiryDialog
        open={capDialog}
        onOpenChange={setCapDialog}
        groupId={gid}
        groupName={groupName}
        defaultDesired={Math.max(memberCount + 10, 60)}
      />
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  suffix,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  suffix?: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 py-4">
        <div className="rounded-lg bg-primary/10 p-2 text-primary">{icon}</div>
        <div className="flex flex-col">
          <span className="text-xs text-muted-foreground">{label}</span>
          <span className="text-lg font-semibold">
            {value}
            {suffix && (
              <span className="ml-0.5 text-sm font-normal text-muted-foreground">
                {suffix}
              </span>
            )}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
